// src/Pages/ReceptionistDashboard/Appointments.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
  Badge,
  Chip,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  EventAvailable as EventAvailableIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AssignmentInd as AssignmentIndIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTodayAppointments,
  updateAppointmentStatus,
} from '../../features/receptionist/receptionistSlice';

const ReceptionistAppointmentManagement = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const { todayAppointments, appointmentsLoading, appointmentsError } = useSelector(
    (state) => state.receptionist
  );
  
  const appointments = todayAppointments || [];
  const loading = appointmentsLoading;
  const [tabValue, setTabValue] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterDoctor, setFilterDoctor] = useState('');
  const [filterPatient, setFilterPatient] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notification, setNotification] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Status colors for chips
  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return 'primary';
      case 'Arrived': return 'warning';
      case 'In Room': return 'info';
      case 'Completed': return 'success';
      case 'Canceled': return 'error';
      default: return 'default';
    }
  };

  // Status icons
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Scheduled': return <ScheduleIcon />;
      case 'Arrived': return <CheckCircleIcon />;
      case 'In Room': return <AssignmentIndIcon />;
      case 'Completed': return <CheckIcon />;
      default: return <InfoIcon />;
    }
  };

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    try {
      await dispatch(fetchTodayAppointments()).unwrap();
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setNotification({
        message: 'Failed to fetch appointments',
        severity: 'error'
      });
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleFilterChange = (event, filterType) => {
    if (filterType === 'doctor') setFilterDoctor(event.target.value);
    if (filterType === 'patient') setFilterPatient(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Update appointment status
  const handleUpdateStatus = async (appointmentId, newStatus) => {
    try {
      await dispatch(updateAppointmentStatus({ appointmentId, status: newStatus })).unwrap();
      
      setNotification({
        message: `Appointment status updated to ${newStatus}`,
        severity: 'success'
      });
      
      // Refresh appointments list
      dispatch(fetchTodayAppointments());
    } catch (error) {
      console.error('Error updating status:', error);
      setNotification({
        message: error || 'Failed to update status',
        severity: 'error'
      });
    }
  };

  const filteredAppointments = appointments
    .filter((appointment) => {
      return (
        (!filterDoctor || appointment.doctor_name === filterDoctor) &&
        (!filterPatient || appointment.patient_name === filterPatient) &&
        (appointment.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          appointment.doctor_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    })
    .filter((appointment) => {
      if (tabValue === 0) return appointment.status === 'Scheduled';
      if (tabValue === 1) return appointment.status === 'Arrived';
      if (tabValue === 2) return appointment.status === 'In Room';
      if (tabValue === 3) return appointment.status === 'Completed';
      return true;
    });

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      case 'Scheduled': return 'Arrived';
      case 'Arrived': return 'In Room';
      case 'In Room': return 'Completed';
      default: return null;
    }
  };

  const getActionButton = (appointment) => {
    const nextStatus = getNextStatus(appointment.status);
    if (!nextStatus) return null;

    const getButtonProps = () => {
      switch (nextStatus) {
        case 'Arrived':
          return {
            color: 'warning',
            icon: <CheckCircleIcon />,
            label: 'Mark Arrived'
          };
        case 'In Room':
          return {
            color: 'info',
            icon: <AssignmentIndIcon />,
            label: 'Mark In Room'
          };
        case 'Completed':
          return {
            color: 'success',
            icon: <CheckIcon />,
            label: 'Mark Completed'
          };
        default:
          return null;
      }
    };

    const buttonProps = getButtonProps();
    if (!buttonProps) return null;

    return (
      <Button
        variant="outlined"
        color={buttonProps.color}
        startIcon={buttonProps.icon}
        onClick={() => handleUpdateStatus(appointment.id, nextStatus)}
        size="small"
      >
        {buttonProps.label}
      </Button>
    );
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h4' gutterBottom>
          Appointment Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchAppointments}
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Filters */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label='Search by Patient or Doctor Name'
            variant='outlined'
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel>Filter by Doctor</InputLabel>
            <Select
              value={filterDoctor}
              onChange={(e) => handleFilterChange(e, 'doctor')}
              label='Filter by Doctor'
            >
              <MenuItem value=''>All Doctors</MenuItem>
              {Array.from(new Set(appointments.map(a => a.doctor_name))).map((doctor) => (
                <MenuItem key={doctor} value={doctor}>{doctor}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="appointment status tabs">
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Scheduled" color="primary" size="small" />
                <Badge badgeContent={appointments.filter(a => a.status === 'Scheduled').length} color="primary" />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Arrived" color="warning" size="small" />
                <Badge badgeContent={appointments.filter(a => a.status === 'Arrived').length} color="warning" />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="In Room" color="info" size="small" />
                <Badge badgeContent={appointments.filter(a => a.status === 'In Room').length} color="info" />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Completed" color="success" size="small" />
                <Badge badgeContent={appointments.filter(a => a.status === 'Completed').length} color="success" />
              </Box>
            } 
          />
        </Tabs>
      </Box>

      {/* Appointments List */}
      {filteredAppointments.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm || filterDoctor ? 'No appointments match your search criteria' : 'No appointments for selected status'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredAppointments.map((appointment) => (
            <Grid item xs={12} key={appointment.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="h6">
                          {appointment.patient_name}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(appointment.status)}
                          label={appointment.status}
                          color={getStatusColor(appointment.status)}
                          size="small"
                        />
                      </Box>
                      
                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DoctorIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Dr. {appointment.doctor_name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {appointment.scheduled_time}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          ₹{appointment.consultation_fee}
                        </Typography>
                      </Stack>
                      
                      <Typography variant="body2" color="text.secondary">
                        Phone: {appointment.patient_phone}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {getActionButton(appointment)}
                      
                      <Tooltip title="View Details">
                        <IconButton 
                          color="info" 
                          size="small"
                          onClick={() => {
                            setSelectedAppointment(appointment);
                            setDrawerOpen(true);
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Appointment Details Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => {}}
      >
        <Box sx={{ width: 350, p: 2 }}>
          {selectedAppointment && (
            <>
              <Typography variant="h6" gutterBottom>
                Appointment Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Patient</Typography>
                  <Typography variant="body1">{selectedAppointment.patient_name}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Doctor</Typography>
                  <Typography variant="body1">Dr. {selectedAppointment.doctor_name}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                  <Typography variant="body1">{selectedAppointment.scheduled_time}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedAppointment.status)}
                    label={selectedAppointment.status}
                    color={getStatusColor(selectedAppointment.status)}
                  />
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Consultation Fee</Typography>
                  <Typography variant="body1">₹{selectedAppointment.consultation_fee}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedAppointment.patient_phone}</Typography>
                </Box>
              </Stack>
              
              <Box sx={{ mt: 3 }}>
                {selectedAppointment && getActionButton(selectedAppointment)}
              </Box>
            </>
          )}
        </Box>
      </SwipeableDrawer>

      {/* Notification Snackbar */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.severity}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceptionistAppointmentManagement;
